import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { buildApiUrl } from "@/config/api";
import { useLoginStateContext } from "@/pages/sys/login/providers/login-provider";
import { useUserActions, useUserInfo, useUserToken } from "@/store/userStore";
import { Button } from "@/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import ChangePasswordDialog from "./change-password-dialog";
import { usePushSubscription } from "@/hooks/use-push-subscription";

// Helper function to get user initials
const getUserInitials = (username: string) => {
	if (!username) return "U";
	const nameParts = username.trim().split(" ");
	if (nameParts.length >= 2) {
		return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
	}
	return nameParts[0][0].toUpperCase();
};

/**
 * Account Dropdown
 */
export default function AccountDropdown() {
	const userInfo = useUserInfo();
	const userToken = useUserToken();
	const { clearUserInfoAndToken } = useUserActions();
	const { backToLogin } = useLoginStateContext();
	const { t } = useTranslation();
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
	const push = usePushSubscription();

	const logout = async () => {
		setIsLoggingOut(true);

		try {
			// Send logout request to API
			const response = await fetch(buildApiUrl("/logout"), {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: `Bearer ${userToken.accessToken}`,
				},
				body: JSON.stringify({
					userid: userInfo.id,
					token: userToken.accessToken,
				}),
			});

			if (!response.ok) {
				throw new Error("Logout request failed");
			}

			const result = await response.json();

			// Clear session storage
			sessionStorage.removeItem("token");
			sessionStorage.removeItem("permissions");
			sessionStorage.removeItem("user");

			// Clear store
			clearUserInfoAndToken();

			// Show success message
			toast.success(result.message || "Sikeres kijelentkezés", {
				closeButton: true,
			});

			// Redirect to login
			backToLogin();
		} catch (error) {
			toast.error("Hiba történt a kijelentkezés során", {
				closeButton: true,
			});
		} finally {
			setIsLoggingOut(false);
		}
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon" className="rounded-full">
					<div className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
						{getUserInitials(userInfo.username || "")}
					</div>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-56">
				<div className="flex items-center gap-2 p-2">
					<div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
						{getUserInitials(userInfo.username || "")}
					</div>
					<div className="flex flex-col items-start">
						<div className="text-text-primary text-sm font-medium">{userInfo.username}</div>
						<div className="text-text-secondary text-xs">{userInfo.email}</div>
					</div>
				</div>
				<DropdownMenuSeparator />
				<DropdownMenuItem onClick={() => setIsChangePasswordOpen(true)}>
					Jelszó módosítása
				</DropdownMenuItem>
				{/* Always show the PWA install trigger — the browser decides in
				    the modal itself whether the native prompt is available or the
				    iOS "Add to home screen" instructions should render. */}
				<DropdownMenuItem onClick={() => window.dispatchEvent(new CustomEvent("pwa-open-install"))}>
					📱 Alkalmazás telepítése
				</DropdownMenuItem>
				{/* Push notification opt-in — one click subscribe, one click test
				    dispatch to verify delivery. Hidden if the browser can't do push. */}
				{push.state.supported && (
					push.state.subscribed ? (
						<>
							<DropdownMenuItem
								disabled={push.busy}
								onClick={async () => {
									const r = await push.sendTest();
									(r.ok ? toast.success : toast.error)(r.msg);
								}}
							>
								🔔 Teszt értesítés küldése
							</DropdownMenuItem>
							<DropdownMenuItem
								disabled={push.busy}
								onClick={async () => {
									const r = await push.unsubscribe();
									(r.ok ? toast.success : toast.error)(r.msg);
								}}
							>
								🔕 Értesítések kikapcsolása
							</DropdownMenuItem>
						</>
					) : (
						<DropdownMenuItem
							disabled={push.busy}
							onClick={async () => {
								const r = await push.subscribe();
								if (r.ok) {
									toast.success(r.msg);
									// Fire-and-forget a test push so the user sees it works.
									setTimeout(() => push.sendTest(), 500);
								} else {
									toast.error(r.msg);
								}
							}}
						>
							🔔 Értesítések engedélyezése
						</DropdownMenuItem>
					)
				)}
				<DropdownMenuSeparator />
				<DropdownMenuItem className="font-bold text-warning" onClick={logout} disabled={isLoggingOut}>
					{isLoggingOut ? "Kijelentkezés..." : t("sys.login.logout")}
				</DropdownMenuItem>
			</DropdownMenuContent>
			<ChangePasswordDialog
				open={isChangePasswordOpen}
				onOpenChange={setIsChangePasswordOpen}
			/>
		</DropdownMenu>
	);
}
